import { PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { COMPANY_NAME_FC } from 'config'
import { DateTime } from 'luxon'
import { docCategory } from 'pages/LoanSubmission/constants'
import { useEffect, useState } from 'react'
import { getPurchaseAdviceData, postPurchaseAdviceDate, sendPurchaseAdviceDataToAccounting } from 'services'
import Api from 'services/api'
import { Button, ButtonGroup, Checkbox, Tooltip } from 'stories/components'
import { OverrideCaclModal } from 'stories/components/OverrideCalcModal/OverrideCalcModal'
import { confirm, getPrice2decimal, getPrice3decimal, normalizeDateYMD, Num, openAuditLog, removeComma } from 'utils'
import { calculateDayDifference30 } from 'utils/convertor'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { getDailyRateYearDays, isThirtyDayMonthBasis } from '../dailyRateBasis'
import { fetchProjectedPurchaseAdviceValues, showPurchaseAdviceInterestCalculation } from '../fillProjected'
import { purchaseAdviceAccounting, purchaseAdviceGeneralInfos, purchaseAdvicePostClosing } from './constant'

const tabs = ['Default', 'Sweep']

const calcKeys = [
  'PAInterestDays',
  'PAInterestAmount',
  'PAPremiumOrDiscountCalc',
  'PA_SRPAmountCalc',
  'PAWireAmount',
  'InterestSpread',
]

export default function PurchaseAdvice() {
  const [_data, _setData] = useState<any>({})
  const [sweepData, setSweepData] = useState<any>({})
  const [IDs, setIDs] = useState<any>({})
  const [action, setAction] = useState('')
  const [servicingStatus, setServicingStatus] = useState('')
  const [purchaseAdviceGeneralInfosFields, setPurchaseAdviceGeneralInfosFields] =
    useState<any>(purchaseAdviceGeneralInfos)
  const [purchaseAdviceAccountingFields, setPurchaseAdviceAccountingFields] = useState<any>(purchaseAdviceAccounting)
  const [isDutch, setIsDutch] = useState(false)
  const [changed, setChanged] = useState(false)
  const [tab, setTab] = useState('Default')
  const [ovData, setOVData] = useState<any>({})
  const [loanMeta, setLoanMeta] = useState<{ isBPiece?: boolean; aPieceAmount?: any; proposedLoanAmount?: any }>({})

  const loanNumber = Api.getLoanNumber()
  const initData = async () => {
    setAction('init')
    const res = await getPurchaseAdviceData()
    setAction('')
    if (res.success) {
      setServicingStatus(res.data.servicingStatus)
      _setData(res.data.data)
      setSweepData(res.data.sweepData)
      setIDs(res.data.IDs)
      setLoanMeta({
        isBPiece: res.data.isBPiece,
        aPieceAmount: res.data.aPieceAmount,
        proposedLoanAmount: res.data.proposedLoanAmount,
      })
      let temp = cloneDeep(purchaseAdviceGeneralInfosFields)
      temp['InvestorCode'].options = res.data.investors
      setPurchaseAdviceGeneralInfosFields(temp)

      if (showPurchaseAdviceInterestCalculation(res.data.data.ProductType)) {
        let tempPostClosing = cloneDeep(purchaseAdviceAccountingFields)
        tempPostClosing['PAUnpaidPrincipalBalance'].span = 4
        tempPostClosing['InterestCalculation'].visible = true
        setPurchaseAdviceAccountingFields(tempPostClosing)
        if (res.data.data.InterestCalculation === 'Dutch') setIsDutch(true)
      }
    }
  }

  useEffect(() => {
    setLoanNumber()
    initData()
  }, [])

  const data = tab === 'Default' ? _data : sweepData
  const setData = tab === 'Default' ? _setData : setSweepData

  const validateSync = async () => {
    if (
      servicingStatus === 'purchased' &&
      removeComma(data.PAWireAmountOV || calcValue('PAWireAmount')) !==
        removeComma(getPrice2decimal(data['WireAmountReceived']))
    ) {
      const content = (
        <div className="text-gray-500 text-[16px] mb-6">
          The <b className="text-gray-600">'Investor Wire Amount'</b> and{' '}
          <b className="text-gray-600">'Investor Wire Amount Received'</b> do not match. Are you sure you still want to
          proceed with accepting these values?
        </div>
      )
      const result = await confirm(content, { titleYes: 'Accept Values' })
      if (!result) return false
      return true
    }
    return true
  }

  const onSync = async (key?: string, value?: any) => {
    console.log(key, value)
    const validate = await validateSync()
    if (!validate) return
    setAction('sync')
    setChanged(false)
    let _data = cloneDeep(data)
    if (key) _data[key] = value
    _data['PAInterestDays'] = calcValue('PAInterestDays')
    _data['PAInterestAmount'] = calcValue('PAInterestAmount')
    await postPurchaseAdviceDate({ IDs, data: _data, key: tab === 'Default' ? 'info' : 'sweepInfo' })
    setAction('')
  }

  const onSendAccounting = async () => {
    const validate = await validateSync()
    if (!validate) return
    if (changed) await onSync()
    setAction('accounting')
    await sendPurchaseAdviceDataToAccounting(tab === 'Default' ? 'info' : 'sweepInfo')
    setAction('')
  }

  const changeData = (key: string, value: any) => {
    const temp = cloneDeep(data)
    if (
      [
        'PARollFee',
        'PAOtherFees',
        'PAEscrowBalance',
        'PABuydownFunds',
        'APiece',
        'BPiece',
        'PariPassu',
        'ConstructionReserve',
        'InterestReserve',
        'PreviouslySwept',
      ].indexOf(key) !== -1
    ) {
      if (removeComma(value) > 0) value = -1 * removeComma(value)
    }
    temp[key] = value
    if (key === 'InterestCalculation') setIsDutch(value === 'Dutch')
    setChanged(true)
    setData(temp)
  }

  const calcValue = (key: string) => {
    let rlt = 0
    const unpaidBalance = removeComma(data.PAUnpaidPrincipalBalance)
    if (key === 'PAInterestDays') {
      try {
        const paidTo = normalizeDateYMD(data['PurchaseAdviceBuyerPaidToDate'])
        const settlement = normalizeDateYMD(data['PurchaseAdviceBuyerSettlementDate'])
        if (isThirtyDayMonthBasis(data.DailyRateBasis)) {
          rlt = calculateDayDifference30(settlement, paidTo)
        } else {
          const start = DateTime.fromFormat(paidTo, 'yyyy-MM-dd')
          const end = DateTime.fromFormat(settlement, 'yyyy-MM-dd')
          rlt = Math.trunc(start.diff(end, 'days').days) * -1
        }
      } catch {}
      return rlt
    }
    if (key === 'PAInterestAmount') {
      try {
        let currentBalance = removeComma(data['PAUnpaidPrincipalBalance'])
        if (!isDutch) {
          currentBalance = Num(data['PAUnpaidPrincipalBalance']) + Num(data['ConstructionReserve'])
        }
        const rate = removeComma(data['BuyRate'])
        const interestDays = removeComma(data.PAInterestDaysOV || calcValue('PAInterestDays'))
        const yearDays = getDailyRateYearDays(data.DailyRateBasis)
        rlt = ((currentBalance * rate) / 100 / yearDays) * interestDays
      } catch {}
    }
    if (key === 'PAPremiumOrDiscountCalc') {
      const netPrice = removeComma(data.PANetPrice)
      if (netPrice !== 0) {
        rlt = (-1 * unpaidBalance * (100 - netPrice)) / 100
      }
    }
    if (key === 'PA_SRPAmountCalc') {
      const srpPerc = removeComma(data.PA_SRPPerc)
      if (srpPerc !== 0) {
        rlt = (unpaidBalance * srpPerc) / 100
      }
    }
    if (key === 'PAWireAmount') {
      rlt =
        removeComma(data.PAUnpaidPrincipalBalance) +
        removeComma(data.PARollFee) +
        removeComma(data.PAOtherFees) +
        removeComma(data.PAEscrowBalance) +
        removeComma(data.APiece) +
        removeComma(data.BPiece) +
        removeComma(data.PariPassu) +
        removeComma(data.ConstructionReserve) +
        removeComma(data.InterestReserve) +
        removeComma(data.PAInterestAmountOV || calcValue('PAInterestAmount')) +
        removeComma(data.PABuydownFunds) +
        Num(data.PreviouslySwept)
      if (removeComma(data.PAPremiumOrDiscountOV) !== 0) {
        rlt += removeComma(data.PAPremiumOrDiscountOV)
      } else {
        rlt += removeComma(data.PAPremiumOrDiscountOV || calcValue('PAPremiumOrDiscountCalc'))
      }
      if (removeComma(data.PA_SRPAmountOV) !== 0) {
        rlt += removeComma(data.PA_SRPAmountOV)
      } else {
        rlt += removeComma(data.PA_SRPAmountCalcOV || calcValue('PA_SRPAmountCalc'))
      }
      rlt = Number(rlt.toFixed(2))
    }
    if (key === 'InterestSpread') {
      return getPrice3decimal(Num(data.NoteRate) - Num(data.BuyRate), false, true)
    }
    return rlt.toFixed(2)
  }

  const showHistory = (key: string) => {
    const options = {
      table: 'PurchaseBuyer',
      field: `${key}`,
      keys: {
        field: key,
        type: tab === 'Default' ? 'default' : 'sweep',
      },
    }
    openAuditLog(options)
  }

  const closeOVModal = async (save: boolean, modalData: any) => {
    console.log(ovData, modalData)
    if (save) {
      changeData(ovData.overrideKey, modalData.ovValue)
      await onSync(ovData.overrideKey, modalData.ovValue)
    }
    setOVData({})
  }

  /** Copy every listed key from sweep, including empty string / null / false / 0. If sweep omits a key, clear it on default. */
  const assignFromSweep = (target: Record<string, any>, source: Record<string, any>, keys: string[]) => {
    keys.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = cloneDeep(source[key])
      } else {
        target[key] = undefined
      }
    })
  }

  const copyGeneralInfoFromSweep = () => {
    const next = cloneDeep(_data)
    assignFromSweep(next, sweepData, Object.keys(purchaseAdviceGeneralInfos))
    _setData(next)
    setChanged(true)
  }

  const copyInvestorWireFromSweep = () => {
    const next = cloneDeep(_data)
    assignFromSweep(next, sweepData, Object.keys(purchaseAdviceAccounting))
    calcKeys.forEach((key) => {
      const ovKey = `${key}OV`
      if (Object.prototype.hasOwnProperty.call(sweepData, ovKey)) {
        next[ovKey] = cloneDeep(sweepData[ovKey])
      } else {
        next[ovKey] = undefined
      }
    })
    setIsDutch(next.InterestCalculation === 'Dutch')
    _setData(next)
    setChanged(true)
  }

  const fillProjected = async () => {
    if (!_data.InvestorFirstPaymentDate) return
    setAction('fillProjected')
    try {
      const projected = await fetchProjectedPurchaseAdviceValues(_data.InvestorFirstPaymentDate, 'buyer', loanMeta)
      const next = cloneDeep(_data)
      Object.entries(projected).forEach(([key, value]) => {
        if (value !== undefined) next[key] = value
      })
      _setData(next)
      setChanged(true)
    } finally {
      setAction('')
    }
  }

  return (
    <div className="purchase-advice-container px-2">
      <SaveChanges show={changed} onSave={onSync} />
      {/* <Overview title="Purchase Advice Buyer" /> */}
      <div className="max-w-screen-2xl m-auto">
        {/* <div className="relative bg-white shadow1 rounded mb-4 md:p-6 p-2"> */}
        <div className="relative bg-white">
          <LayoutLoading show={action !== ''} />
          <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
            <span>Purchase Advice Buyer</span>
          </h2>
          <ButtonGroup title={tabs} value={tab} onChange={setTab} />
          <div className="grid md:grid-cols-12 gap-5">
            <div className="md:col-span-5">
              <div className="shadow rounded border">
                <div className="bg-gray-200 round-t py-1 px-4">Accounting / Post Closing</div>
                <div className="p-2">
                  {Object.keys(purchaseAdvicePostClosing).map((key, index) => {
                    let input = purchaseAdvicePostClosing[key]
                    input.value = data[key]
                    input.history = true
                    if (input.visible === false) return null
                    return (
                      <div className="py-[5px]" key={index}>
                        <RenderInput input={input} Key={key} onChange={changeData} showHistory={showHistory} />
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="shadow rounded border mt-4">
                <div className="bg-gray-200 round-t py-1 px-4 flex justify-between items-center gap-2">
                  <span>General Info & Date</span>
                  {tab === 'Default' && (
                    <span
                      className="text-sm text-shade-blue cursor-pointer hover:underline shrink-0 py-0"
                      onClick={copyGeneralInfoFromSweep}
                    >
                      Copy from Sweep
                    </span>
                  )}
                </div>
                <div className="p-2">
                  {Object.keys(purchaseAdviceGeneralInfosFields).map((key, index) => {
                    let input = purchaseAdviceGeneralInfosFields[key]
                    input.value = data[key]
                    input.history = true
                    if (input.visible === false) return null
                    return (
                      <div className="py-[5px]" key={index}>
                        <RenderInput input={input} Key={key} onChange={changeData} showHistory={showHistory} />
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="shadow rounded border mt-4">
                <div className="bg-gray-200 round-t py-1 px-4">Purchase Advice Notes</div>
                <div className="p-2">
                  <RenderInput
                    input={{
                      title: 'Notes',
                      inputType: 'textarea',
                      history: true,
                      value: data['PANotes'] ? data['PANotes'] : '',
                    }}
                    Key={'PANotes'}
                    onChange={changeData}
                    showHistory={showHistory}
                  />
                </div>
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="shadow rounded border">
                <div className="bg-gray-200 round-t py-1 px-4 flex justify-between items-center gap-2">
                  <span>Investor Wire Calculation</span>
                  {tab === 'Default' && (
                    <div className="flex items-center gap-3 shrink-0">
                      <span
                        className="text-sm text-shade-blue cursor-pointer hover:underline py-0"
                        onClick={copyInvestorWireFromSweep}
                      >
                        Copy from Sweep
                      </span>
                      <span
                        className={`text-sm py-0 ${
                          _data.InvestorFirstPaymentDate
                            ? 'text-shade-blue cursor-pointer hover:underline'
                            : 'text-gray-400 pointer-events-none'
                        }`}
                        onClick={fillProjected}
                      >
                        Fill Projected
                      </span>
                    </div>
                  )}
                </div>
                <div className="grid md:grid-cols-6 gap-2 p-2">
                  {Object.keys(purchaseAdviceAccountingFields).map((key, index) => {
                    let input = purchaseAdviceAccountingFields[key]
                    input.value = data[key]
                    input.history = true
                    if (input.visible === false) return null

                    const overrideKey = `${key}OV`
                    let calcValue_result: any = null

                    if (calcKeys.includes(key)) {
                      calcValue_result = calcValue(key)
                      input.value = data[overrideKey] || calcValue_result
                      input.disabled = true

                      const additionalElements = []
                      additionalElements.push(
                        <span
                          className="mx-2 text-shade-blue cursor-pointer"
                          onClick={() => {
                            setOVData({
                              title: input.title,
                              overrideKey,
                              calcValue: calcValue_result || '',
                              ovValue: data[overrideKey] || '',
                              input: { ...input, disabled: false, additionalElements: null, readOnly: false },
                            })
                          }}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>,
                      )

                      input.additionalElements = <div className="flex items-center gap-2">{additionalElements}</div>
                    }

                    if (key === 'PAUnpaidPrincipalBalance')
                      input.title = tab === 'Default' ? 'Current Balance' : 'Sweep Balance '
                    return (
                      <div className={`md:col-span-${input.span ? input.span : 6}`} key={index}>
                        <RenderInput input={input} Key={key} onChange={changeData} showHistory={showHistory} />
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="p-2 grid md:grid-cols-6 gap-2 my-2">
                <div className="md:col-span-2">
                  <RenderInput
                    input={{
                      title: `Taxes to be paid by ${COMPANY_NAME_FC}`,
                      className: 'flex items-center',
                      inputType: 'checkbox',
                      history: true,
                      value:
                        data['PurchaseAdviceBuyerTaxPaid'] !== undefined ? data['PurchaseAdviceBuyerTaxPaid'] : false,
                    }}
                    Key={'PurchaseAdviceBuyerTaxPaid'}
                    onChange={changeData}
                    showHistory={showHistory}
                  />
                </div>
                <div className="md:col-span-2">
                  <RenderInput
                    input={{
                      title: `Insurance to be paid by ${COMPANY_NAME_FC}`,
                      className: 'flex items-center',
                      inputType: 'checkbox',
                      history: true,
                      value:
                        data['PurchaseAdviceBuyerInsurancePaid'] !== undefined
                          ? data['PurchaseAdviceBuyerInsurancePaid']
                          : false,
                    }}
                    Key={'PurchaseAdviceBuyerInsurancePaid'}
                    onChange={changeData}
                    showHistory={showHistory}
                  />
                </div>
                {tab === 'Sweep' && (
                  <div className="md:col-span-2">
                    <RenderInput
                      input={{
                        title: `Sweep`,
                        className: 'flex items-center',
                        inputType: 'checkbox',
                        history: true,
                        value: data['isSweep'] !== undefined ? data['isSweep'] : false,
                      }}
                      Key={'isSweep'}
                      onChange={changeData}
                      showHistory={showHistory}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            <RenderInput
              input={{
                inputType: 'FileTable',
                title: 'Attachment Files',
                required: false,
                filePath: `/loan/${loanNumber}/purchaseAdviceBuyer`,
                span: 'full',
                showCategory: true,
                showStatus: true,
                multiple: true,
                categories: docCategory().filter((v) => !!v),
                additionalActions: [],
                value: data.documents,
                additionalRowActions: (document: any, index: number, onChange: Function) => {
                  return (
                    <Tooltip message="Internal/External">
                      <Checkbox
                        title=""
                        id={`side-document-intext-${document.id}`}
                        checked={document.intext}
                        onChange={(value: any) => onChange('intext', index, value)}
                      />
                    </Tooltip>
                  )
                },
              }}
              Key="documents"
              key="documents"
              onChange={changeData}
            />
          </div>
          <div className="flex justify-center gap-4 mt-4">
            <div className="w-48">
              <Button loading={action === 'sync'} onClick={onSync} className="w-full">
                Sync
              </Button>
            </div>
            <div className="w-48 -mt-2">
              <Button loading={action === 'accounting'} link onClick={onSendAccounting} className="w-full">
                Send to Accounting
              </Button>
            </div>
          </div>
        </div>
      </div>
      {Object.keys(ovData).length > 0 && (
        <OverrideCaclModal
          title={ovData.title}
          calcValue={ovData.calcValue}
          ovValue={ovData.ovValue}
          onClose={closeOVModal}
          input={ovData.input}
        />
      )}
    </div>
  )
}
